import React, { useState } from "react";
import { Box, Grid, Card, CardContent, Typography, LinearProgress, Stack, Chip, Paper, Tab, Tabs } from "@mui/material";
import {
  MortuaryOccupancyReport,
  ifasReportsService,
  IFASReportFilter,
  IFASAdditionalReport,
} from "../../../../../services/ifasReportsService";
import { IFASReportTable, ColumnDef } from "../components/IFASReportTable";
import { ifasColors } from "../../components/OperationsUI";

interface Props {
  mortuaries: MortuaryOccupancyReport[];
  loading: boolean;
  filter: IFASReportFilter;
  mortuaryTrends: IFASAdditionalReport[];
}

export const MortuaryReportsTab: React.FC<Props> = ({ mortuaries, mortuaryTrends, loading, filter }) => {
  const [reportView, setReportView] = useState<"current" | "trend">("current");
  const columns: ColumnDef<MortuaryOccupancyReport>[] = [
    { id: "mortuaryName", label: "Mortuary Facility", minWidth: 180 },
    { id: "province", label: "Province", minWidth: 130 },
    { id: "capacity", label: "Capacity", minWidth: 100, align: "right" },
    { id: "currentBodiesCount", label: "Current Bodies", minWidth: 130, align: "right" },
    {
      id: "occupancyPercentage",
      label: "Occupancy %",
      minWidth: 180,
      format: (val) => {
        const pct = typeof val === "number" ? val : parseFloat(val);
        const color = pct >= 85 ? "error" : pct >= 65 ? "warning" : "success";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: "100%", mr: 1 }}>
              <LinearProgress variant="determinate" value={Math.min(100, pct)} color={color} sx={{ height: 8, borderRadius: 4 }} />
            </Box>
            <Typography variant="body2" fontWeight={700} color={`${color}.main`}>
              {pct}%
            </Typography>
          </Box>
        );
      },
    },
    { id: "avgStorageDurationDays", label: "Avg Storage (Days)", minWidth: 150, align: "right" },
  ];
  const trendColumns: ColumnDef<IFASAdditionalReport>[] = [
    { id: "name", label: "Mortuary", minWidth: 170 },
    { id: "location", label: "Location", minWidth: 150 },
    { id: "date", label: "Date", minWidth: 110 },
    { id: "quantity", label: "Admissions / Releases", minWidth: 160, align: "right" },
    { id: "metric", label: "Occupancy / Forecast %", minWidth: 180, align: "right", format: (value) => value == null ? "-" : `${value}%` },
    { id: "status", label: "Capacity Status", minWidth: 130 },
    { id: "details", label: "Trend & Forecast Details", minWidth: 260 },
  ];

  return (
    <Box>
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {mortuaries.map((m, idx) => {
          const pct = m.occupancyPercentage || 0;
          const statusColor = pct >= 85 ? "#EF4444" : pct >= 65 ? "#F59E0B" : "#10B981";

          return (
            <Grid size={{ xs: 12, sm: 6, md: 6 }} key={idx}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${ifasColors.border}`,
                  background: ifasColors.surface,
                  boxShadow: "0 8px 24px rgba(15,23,42,0.045)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ height: 4, background: statusColor }} />
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight={800} color={ifasColors.text}>
                      {m.mortuaryName}
                    </Typography>
                    <Chip label={m.branchName} size="small" sx={{ bgcolor: ifasColors.page, color: ifasColors.muted, fontWeight: 700, borderRadius: 1.5 }} />
                  </Stack>

                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ my: 1.5 }}>
                    <Typography variant="h3" fontWeight={800} color={statusColor}>
                      {m.currentBodiesCount}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" fontWeight={600}>
                      / {m.capacity} Total Capacity
                    </Typography>
                  </Stack>

                  <Box sx={{ mb: 2 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        Capacity Utilisation
                      </Typography>
                      <Typography variant="caption" fontWeight={800} color={statusColor}>
                        {m.occupancyPercentage}%
                      </Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={Math.min(100, pct)} sx={{ height: 8, borderRadius: 4, bgcolor: ifasColors.border, "& .MuiLinearProgress-bar": { backgroundColor: statusColor, borderRadius: 4 } }} />
                  </Box>

                  <Grid container spacing={1} sx={{ pt: 1, borderTop: `1px solid ${ifasColors.border}` }}>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Available</Typography>
                      <Typography variant="subtitle2" fontWeight={800} color="#059669">{m.availableCapacity} Units</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Released</Typography>
                      <Typography variant="subtitle2" fontWeight={800} color="#3B82F6">{m.bodiesReleasedPeriod}</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Awaiting Release</Typography>
                      <Typography variant="subtitle2" fontWeight={800} color="#D97706">{m.bodiesAwaitingRelease}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper elevation={0} sx={{ mb: 2, p: 0.65, borderRadius: "14px", border: `1px solid ${ifasColors.border}` }}>
        <Tabs value={reportView} onChange={(_, value) => setReportView(value)} sx={{ minHeight: 40, "& .MuiTabs-indicator": { display: "none" }, "& .MuiTab-root": { minHeight: 38, borderRadius: "9px", px: 2.5, py: 1, color: ifasColors.muted, fontSize: "0.72rem", fontWeight: 700, textTransform: "none" }, "& .Mui-selected": { bgcolor: ifasColors.softOrange, color: `${ifasColors.deepOrange} !important` } }}>
          <Tab value="current" label={`Current Occupancy (${mortuaries.length})`} />
          <Tab value="trend" label={`Trend & Forecast (${mortuaryTrends.length})`} />
        </Tabs>
      </Paper>

      {reportView === "current" && <IFASReportTable
        title="Mortuary Utilisation & Occupancy Trend Report"
        subtitle="Real-time capacity, occupancy rate, received/released statistics and storage duration"
        columns={columns}
        data={mortuaries}
        loading={loading}
        onExport={(fmt) => ifasReportsService.downloadReportExport("mortuary-occupancy", fmt, filter)}
      />}
      {reportView === "trend" && <IFASReportTable
        title="Mortuary Occupancy Trend & Capacity Forecast"
        subtitle="Historical admissions, releases, occupancy and seven-day capacity forecast"
        columns={trendColumns}
        data={mortuaryTrends}
        loading={loading}
        onExport={(fmt) => ifasReportsService.downloadReportExport("mortuary-trends", fmt, filter)}
      />}
    </Box>
  );
};
